'use client'

import {useIsGamePage} from '@app/[lang]/(common-header-footer)/content-wrapper/content-wrapper-client'
import {useTagsPageAnalytics} from '@app/[lang]/(common-header-footer)/tags/(tag-page)/[tagName]/[tagId]/get-tag-page-analytics/tag-page-analytics-context'
import {LanguageCodes} from '@app/i18n/settings'
import triggerAnalytics from '@gamezop/analytics-sdk/client/trigger-analytics'
import {createContext, useContext, useState, ReactNode} from 'react'

type SidebarContextType = {
  isOpen: boolean
  openSidebar: () => void
  closeSidebar: () => void
  toggleSidebar: () => void
  rawOpenSidebar: () => void
  rawHideSidebar: () => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

type SidebarProviderProps = {
  children: ReactNode
  defaultOpen?: boolean
  lang: LanguageCodes
}

export const SidebarProvider = ({children, defaultOpen = false, lang}: SidebarProviderProps) => {
  const {isGamePage} = useIsGamePage()
  const defaultSidebarOpen = defaultOpen && !isGamePage
  const [isOpen, setIsOpen] = useState(defaultSidebarOpen)
  const {tagAnalyticsData} = useTagsPageAnalytics()

  const openAndSendAnalytics = () => {
    setIsOpen(true)
    triggerAnalytics('side-menu-open', tagAnalyticsData).sendAll()
  }
  const closeAndSendAnalytics = () => {
    setIsOpen(false)
    triggerAnalytics('side-menu-closed', tagAnalyticsData).sendAll()
  }

  const openSidebar = () => openAndSendAnalytics()
  const closeSidebar = () => closeAndSendAnalytics()
  const toggleSidebar = () => (isOpen ? closeAndSendAnalytics() : openAndSendAnalytics())
  const rawOpenSidebar = () => setIsOpen(true)
  const rawHideSidebar = () => setIsOpen(false)

  return (
    <SidebarContext.Provider value={{isOpen, openSidebar, closeSidebar, toggleSidebar, rawOpenSidebar, rawHideSidebar}}>
      {children}
    </SidebarContext.Provider>
  )
}

export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext)
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}
